const CHUNK_RECOVERY_STATE_KEY = 'profiteia:chunk-load-recovery';
const DEFAULT_MAX_ATTEMPTS = 2;
const DEFAULT_ATTEMPT_WINDOW_MS = 60_000;

export function isChunkLoadError(error) {
  const message = [
    error?.name,
    error?.message,
    error?.reason?.name,
    error?.reason?.message,
    typeof error === 'string' ? error : '',
  ]
    .filter(Boolean)
    .join(' ');

  // ponytail: only webpack's own "CSS " infix is allowed between "loading" and
  // "chunk", so unrelated errors merely containing "chunk" still don't match.
  return /chunkloaderror|loading (?:css )?chunk \S+ failed|error loading dynamically imported module|failed to fetch dynamically imported module/i.test(
    message
  );
}

function readRecoveryState(storage, now, attemptWindowMs) {
  try {
    const raw = storage?.getItem(CHUNK_RECOVERY_STATE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || now - Number(parsed.firstAttemptAt || 0) > attemptWindowMs) {
      return { firstAttemptAt: now, attempts: 0 };
    }
    return {
      firstAttemptAt: Number(parsed.firstAttemptAt || now),
      attempts: Number(parsed.attempts || 0),
    };
  } catch {
    return { firstAttemptAt: now, attempts: 0 };
  }
}

function writeRecoveryState(storage, state) {
  try {
    storage?.setItem(CHUNK_RECOVERY_STATE_KEY, JSON.stringify(state));
  } catch {
    // Storage may be unavailable in hardened browser contexts; recovery can
    // still proceed, just without loop protection.
  }
}

export function clearChunkLoadRecoveryState(storage = window.sessionStorage) {
  try {
    storage?.removeItem(CHUNK_RECOVERY_STATE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

export async function clearBrowserAssetCaches({
  cachesRef = typeof window !== 'undefined' ? window.caches : null,
  navigatorRef = typeof navigator !== 'undefined' ? navigator : null,
} = {}) {
  const tasks = [];

  if (cachesRef?.keys) {
    tasks.push(
      cachesRef
        .keys()
        .then((names) => Promise.all(names.map((name) => cachesRef.delete(name))))
    );
  }

  if (navigatorRef?.serviceWorker?.getRegistrations) {
    tasks.push(
      navigatorRef.serviceWorker
        .getRegistrations()
        .then((registrations) =>
          Promise.all(registrations.map((registration) => registration.unregister()))
        )
    );
  }

  await Promise.allSettled(tasks);
}

export function buildChunkRecoveryUrl(locationRef = window.location, now = Date.now()) {
  const url = new URL(locationRef.href);
  url.searchParams.set('_pf_chunk_reload', String(now));
  return url.toString();
}

export async function recoverFromChunkLoadError(error, options = {}) {
  if (!isChunkLoadError(error)) {
    return false;
  }

  const {
    attemptWindowMs = DEFAULT_ATTEMPT_WINDOW_MS,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    now = Date.now(),
    storage = typeof window !== 'undefined' ? window.sessionStorage : null,
    locationRef = typeof window !== 'undefined' ? window.location : null,
    reload = (url) => window.location.replace(url),
    onExhausted = null,
    onRecovering = null,
    cacheOptions = {},
  } = options;

  const state = readRecoveryState(storage, now, attemptWindowMs);
  if (state.attempts >= maxAttempts) {
    onExhausted?.(error);
    return false;
  }

  const nextState = {
    firstAttemptAt: state.firstAttemptAt,
    attempts: state.attempts + 1,
  };
  writeRecoveryState(storage, nextState);
  onRecovering?.(error, nextState);

  await clearBrowserAssetCaches(cacheOptions);

  if (locationRef) {
    reload(buildChunkRecoveryUrl(locationRef, now));
  }
  return true;
}

export function installRouterChunkLoadRecovery(router, options = {}) {
  router.onError((error) => {
    recoverFromChunkLoadError(error, options);
  });

  router
    .isReady()
    .then(() => clearChunkLoadRecoveryState(options.storage))
    .catch(() => {});
}

export function installGlobalChunkLoadRecovery(options = {}) {
  if (typeof window === 'undefined') {
    return;
  }

  window.addEventListener('unhandledrejection', (event) => {
    if (!isChunkLoadError(event.reason)) {
      return;
    }
    event.preventDefault();
    recoverFromChunkLoadError(event.reason, options);
  });
}
